import Cookies from "js-cookie";
import React from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { ThreeDots } from "react-loader-spinner";
import api from "src/rest/api";

// Captura erros globais
window.onerror = (message, source, lineno, colno, error) => {
  api
    .post("/report/log-error", {
      error: error?.message,
      errorInfo: {
        message: typeof message === "string" ? message : message.type,
        source,
        lineno,
        colno,
      },
      origin: window.location.href,
      type: "global",
    })
    .catch((err) => console.error("Falha ao enviar log global:", err));
};

window.onunhandledrejection = (event) => {
  api
    .post("/report/log-error", {
      reason: event.reason,
    })
    .catch((err) =>
      console.error("Falha ao enviar log de promessa rejeitada:", err),
    );
};

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  logSent: boolean; // Para indicar se o log já foi enviado
  isSending: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      logSent: false,
      isSending: false, // Estado para controlar o envio
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
      logSent: false,
      isSending: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ error, errorInfo });
  }

  private getCacheData(): Record<string, string> {
    try {
      return {
        localStorage: JSON.stringify(localStorage),
        sessionStorage: JSON.stringify(sessionStorage),
        cookies: JSON.stringify(Cookies.get()),
      };
    } catch (err) {
      return {
        error: "Failed to retrieve cache data",
        details: JSON.stringify(err),
      };
    }
  }

  private async sendErrorLog(): Promise<void> {
    const { error, errorInfo } = this.state;

    this.setState({ isSending: true }); // Inicia o estado de envio

    try {
      await api.post("/report/log-error", {
        error: error?.message,
        errorInfo: {
          componentStack: errorInfo?.componentStack,
          stack: error?.stack, // Adiciona a stack do erro
        },
        cacheData: this.getCacheData(),
        origin: window.location.href,
        type: "user",
      });
      this.setState({ logSent: true });
      window.location.reload();
    } catch (err) {
      console.error("Erro ao enviar o log:", err);
    } finally {
      this.setState({ isSending: false }); // Finaliza o estado de envio
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={styles.errorContainer}>
          <FaExclamationTriangle style={styles.icon} />
          <h1 style={styles.title}>Ocorreu um erro inesperado</h1>
          <p style={styles.description}>
            Um erro ocorreu e interrompeu a aplicação. Você pode tentar
            novamente mais tarde.
          </p>
          {!this.state.logSent && (
            <button
              style={{
                ...styles.button,
                cursor: this.state.isSending ? "not-allowed" : "pointer",
                opacity: this.state.isSending ? 0.6 : 1,
              }}
              onClick={() => this.sendErrorLog()}
              disabled={this.state.isSending} // Desativa o botão enquanto envia
            >
              {this.state.isSending ? (
                <ThreeDots
                  height="20"
                  width="20"
                  color="#fff"
                  ariaLabel="Enviando..."
                />
              ) : (
                "Enviar Relatório de Erro"
              )}
            </button>
          )}
          {this.state.logSent && (
            <p style={styles.logSentMessage}>
              Relatório de erro enviado. Obrigado!
            </p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

const styles = {
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    textAlign: "center",
    backgroundColor: "#f8f9fa",
    color: "#343a40",
  },
  icon: {
    fontSize: "4rem",
    color: "#dc3545",
    marginBottom: "1rem",
  },
  title: {
    fontSize: "2rem",
    marginBottom: "0.5rem",
  },
  description: {
    fontSize: "1.2rem",
    marginBottom: "1.5rem",
  },
  button: {
    padding: "0.8rem 1.5rem",
    fontSize: "1rem",
    color: "#fff",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  logSentMessage: {
    fontSize: "1rem",
    color: "#28a745",
  },
} as const;

export default ErrorBoundary;
